/**
 * Role2BasicFacility.js
 * PSP/CRE Tool v2.0 - Medical Facility Models
 * 
 * Role 2 Basic Facility - Brigade Medical Company
 * Forward resuscitation capability with limited surgery
 * 
 * Capabilities:
 * - All Role 1 capabilities PLUS:
 * - Basic resuscitation
 * - Blood transfusion
 * - Limited emergency surgery (damage control)
 * - X-ray capability
 * - Basic laboratory
 * - Hold time: Up to 72 hours
 * - Surgical teams: 1-2 teams
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 26, 2024
 * Version: 2.0 - Phase 2 Evacuation Asset Architecture
 * 
 * PHASE 2 CHANGES:
 * - Added organic evacuation assets (16 ambulances per Role 2B)
 * - Added PULL method as default (Role 2 pulls from Role 1s)
 * - Added helicopter on-call allocation
 * - Added multi-source pickup configuration
 */

import { BaseMedicalFacility, EVACUATION_METHODS } from './BaseMedicalFacility.js';

// ============================================================================
// ROLE 2 BASIC ORGANIC ASSET STANDARDS
// ============================================================================

/**
 * Default organic assets for Role 2 Basic (Brigade Medical Company)
 * Based on NATO standards and Romanian Army TO&E
 * 
 * Typical Role 2B has:
 * - 8 wheeled light ambulances (rear evacuation)
 * - 8 wheeled armored ambulances (forward evacuation from Role 1)
 * - 0 organic helicopters (allocated from higher on request)
 * - Medical company personnel
 */
export const ROLE2_BASIC_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled_light: 8,
      wheeled_armored: 8,
      tracked: 0
    },
    helicopters: {
      allocated: 0,     // No organic helicopters
      on_call: 2        // Typical allocation from division/corps
    },
    personnel: {
      medics: 24,
      drivers: 16,
      litter_bearers: 24,
      flight_medics: 0
    }
  },
  
  LIGHT_BRIGADE: {
    // Light/airborne brigade - fewer vehicles, may have helicopter priority
    ground_ambulances: {
      wheeled_light: 6,
      wheeled_armored: 4,
      tracked: 0
    },
    helicopters: {
      allocated: 0,
      on_call: 3        // Higher helicopter priority for light forces
    },
    personnel: {
      medics: 18,
      drivers: 10,
      litter_bearers: 16,
      flight_medics: 2
    }
  },
  
  HEAVY_BRIGADE: {
    // Armored/mechanized brigade - tracked ambulances, more ground assets
    ground_ambulances: {
      wheeled_light: 6,
      wheeled_armored: 6,
      tracked: 4        // M113 ambulance or BTR variants
    },
    helicopters: {
      allocated: 0,
      on_call: 1        // Lower helicopter priority - ground capable
    },
    personnel: {
      medics: 28,
      drivers: 16,
      litter_bearers: 24,
      flight_medics: 0
    }
  },
  
  ARTILLERY_BRIGADE: {
    // Artillery brigade - lighter medical footprint
    ground_ambulances: {
      wheeled_light: 6,
      wheeled_armored: 4,
      tracked: 0
    },
    helicopters: {
      allocated: 0,
      on_call: 1
    },
    personnel: {
      medics: 16,
      drivers: 10,
      litter_bearers: 12,
      flight_medics: 0
    }
  }
};

// ============================================================================
// ROLE 2 BASIC FACILITY CLASS
// ============================================================================

export class Role2BasicFacility extends BaseMedicalFacility {
  /**
   * Create a Role 2 Basic facility
   * 
   * @param {Object} config - Facility configuration
   * @param {string} config.facility_id - Unique ID
   * @param {string} config.facility_name - Human-readable name
   * @param {string} config.parent_unit_id - Parent brigade ID
   * @param {string} [config.brigade_type='STANDARD'] - Brigade type for asset allocation
   * @param {Array} [config.supported_role1s=[]] - Array of Role 1 facility IDs this Role 2 supports
   * @param {number} [config.position_feba_km=20] - Distance from FEBA
   * @param {number} [config.beds=50] - Number of beds
   * @param {number} [config.surgical_teams=2] - Number of surgical teams
   */
  constructor(config) {
    // Determine organic assets based on brigade type
    const brigadeType = config.brigade_type || 'STANDARD';
    const organicAssets = config.organic_assets || 
                          ROLE2_BASIC_ORGANIC_ASSETS[brigadeType] || 
                          ROLE2_BASIC_ORGANIC_ASSETS.STANDARD;
    
    // Get Role 1s this facility supports (for PULL configuration)
    const supportedRole1s = config.supported_role1s || [];
    
    super({
      ...config,
      role_level: 2,
      facility_type: 'ROLE2_BASIC',
      parent_unit_type: 'BRIGADE',
      beds: config.beds || 50,
      surgical_teams: config.surgical_teams || 2,
      icu_beds: 0,              // Role 2 Basic has no formal ICU
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 20,
      organic_assets: organicAssets,
      
      // Phase 2: Role 2 evacuation config - PULL from Role 1s
      evacuation_config: config.evacuation_config || {
        incoming_method: EVACUATION_METHODS.PULL,        // Role 2 PULLS from Role 1s
        outgoing_method: EVACUATION_METHODS.PUSH,        // Role 2 PUSHes to Role 3 (or pulled by Role 2E/3)
        picks_up_from: supportedRole1s, // Role 1s this facility supports
        delivers_to: null,              // Role 2E or Role 3 - set by force structure
        helicopter_priority: 'NORMAL',
        max_hold_time_hours: 72,
        triggers: {
          capacity_threshold: 75,
          time_threshold_hours: 48,
          t1_immediate_evac: true
        }
      }
    });
    
    // Role 2 Basic specific properties
    this.parent_brigade_id = config.parent_brigade_id || config.parent_unit_id;
    this.brigade_type = brigadeType;
    this.hold_time_hours = 72;
    this.can_perform_surgery = true;
    this.can_perform_definitive_surgery = false;
    
    // Surgical capability tracking
    this.surgical_capacity = {
      max_surgeries_per_day: this.surgical_teams * 4,
      surgeries_today: 0,
      surgical_backlog: 0
    };
    
    // Blood bank
    this.blood_bank = config.blood_bank || {
      prbc_units: 0,
      ffp_units: 0,
      platelet_units: 0,
      walking_blood_bank_activated: false
    };
    
    // Track which Role 1s this facility supports
    this.supported_role1s = supportedRole1s;
  }
  
  // ============================================================================
  // OVERRIDE: GET DEFAULT ORGANIC ASSETS
  // ============================================================================
  
  _getDefaultOrganicAssets() {
    return ROLE2_BASIC_ORGANIC_ASSETS[this.brigade_type] || ROLE2_BASIC_ORGANIC_ASSETS.STANDARD;
  }
  
  // ============================================================================
  // PHASE 2: EVACUATION CHAIN MANAGEMENT
  // ============================================================================
  
  /**
   * Add a Role 1 to the pickup list
   * Role 2 will PULL from these facilities
   * 
   * @param {string} role1FacilityId - Role 1 facility ID
   */
  addSupportedRole1(role1FacilityId) {
    if (!this.supported_role1s.includes(role1FacilityId)) {
      this.supported_role1s.push(role1FacilityId);
      this.addPickupSource(role1FacilityId);
    }
  }
  
  /**
   * Remove a Role 1 from support list
   * 
   * @param {string} role1FacilityId - Role 1 facility ID
   */
  removeSupportedRole1(role1FacilityId) {
    this.supported_role1s = this.supported_role1s.filter(id => id !== role1FacilityId);
    this.removePickupSource(role1FacilityId);
  }
  
  /**
   * Get evacuation capacity for pulling from Role 1s
   * Factors in number of supported facilities and distance
   * 
   * @param {Object} role1Distances - Map of role1_id -> distance_km
   * @returns {Object} Pull capacity by Role 1
   */
  getPullCapacityByRole1(role1Distances = {}) {
    const effective = this.getEffectiveAssets();
    const totalAmbulances = effective.ground_ambulances.total;
    
    // Distribute ambulances across supported Role 1s
    const numRole1s = this.supported_role1s.length || 1;
    const ambulancesPerRole1 = Math.floor(totalAmbulances / numRole1s);
    
    const capacityByRole1 = {};
    
    this.supported_role1s.forEach(role1Id => {
      const distance = role1Distances[role1Id] || 12; // Default 12km Role1->Role2
      const tripsPerDay = Math.max(1, 6 * (20 / distance));
      
      capacityByRole1[role1Id] = {
        ambulances_allocated: ambulancesPerRole1,
        distance_km: distance,
        trips_per_day: Math.round(tripsPerDay),
        patients_per_day: Math.round(ambulancesPerRole1 * 4 * tripsPerDay)
      };
    });
    
    return {
      total_ambulances: totalAmbulances,
      supported_role1s: this.supported_role1s.length,
      capacity_by_role1: capacityByRole1,
      total_daily_capacity: Object.values(capacityByRole1)
        .reduce((sum, c) => sum + c.patients_per_day, 0)
    };
  }
  
  /**
   * Dispatch ambulance to pick up from specific Role 1
   * 
   * @param {string} role1FacilityId - Target Role 1 facility
   * @param {string} assetType - Ambulance type
   * @returns {Object} Mission assignment
   */
  dispatchToRole1(role1FacilityId, assetType = 'wheeled_armored') {
    // Validate this Role 1 is in our support list
    if (!this.supported_role1s.includes(role1FacilityId)) {
      return {
        success: false,
        error: 'ROLE1_NOT_SUPPORTED',
        supported_role1s: this.supported_role1s
      };
    }
    
    const effective = this.getEffectiveAssets();
    
    if (effective.ground_ambulances[assetType] <= 0) {
      // Try alternatives
      const alternatives = ['wheeled_armored', 'wheeled_light', 'tracked'];
      for (const alt of alternatives) {
        if (effective.ground_ambulances[alt] > 0) {
          assetType = alt;
          break;
        }
      }
      
      if (effective.ground_ambulances[assetType] <= 0) {
        return {
          success: false,
          error: 'NO_AMBULANCES_AVAILABLE',
          effective_assets: effective
        };
      }
    }
    
    this.stats.ground_evac_missions++;
    
    return {
      success: true,
      mission_type: EVACUATION_METHODS.PULL,
      source_facility: role1FacilityId,
      dest_facility: this.facility_id,
      asset_type: assetType,
      mission_id: `pull_${this.facility_id}_${role1FacilityId}_${this.stats.ground_evac_missions}`,
      timestamp: new Date().toISOString()
    };
  }
  
  /**
   * Request helicopter evacuation
   * Role 2 may have on-call helicopters for urgent cases
   * 
   * @param {Object} patient - Patient requiring air evac
   * @returns {Object} Helicopter request result
   */
  requestHelicopterEvac(patient = {}) {
    const effective = this.getEffectiveAssets();
    
    if (effective.helicopters.on_call <= 0) {
      return {
        success: false,
        error: 'NO_HELICOPTERS_ON_CALL',
        alternative: 'REQUEST_FROM_HIGHER',
        priority: patient.triage_category === 'T1' ? 'URGENT' : 'ROUTINE'
      };
    }
    
    this.stats.air_evac_missions++;
    
    return {
      success: true,
      mission_type: 'MEDEVAC',
      source_facility: this.facility_id,
      patient_priority: patient.triage_category || 'T2',
      mission_id: `medevac_${this.facility_id}_${this.stats.air_evac_missions}`,
      timestamp: new Date().toISOString()
    };
  }
  
  // ============================================================================
  // ROLE 2 SURGICAL & BLOOD BANK METHODS
  // ============================================================================
  
  canPerformSurgery() {
    return true;
  }
  
  performDamageControlSurgery(patient) {
    if (this.surgical_capacity.surgeries_today >= this.surgical_capacity.max_surgeries_per_day) {
      return {
        success: false,
        reason: 'SURGICAL_CAPACITY_EXCEEDED',
        backlog: ++this.surgical_capacity.surgical_backlog
      };
    }
    
    const blood_needed = { prbc: 8, ffp: 8 };
    
    if (this.blood_bank.prbc_units < blood_needed.prbc || 
        this.blood_bank.ffp_units < blood_needed.ffp) {
      return {
        success: false,
        reason: 'INSUFFICIENT_BLOOD',
        needed: blood_needed,
        available: {
          prbc: this.blood_bank.prbc_units,
          ffp: this.blood_bank.ffp_units
        }
      };
    }
    
    const supplies_result = this.consumeSupply('surgical_supplies', 1);
    if (!supplies_result.success) {
      return { success: false, reason: 'NO_SURGICAL_SUPPLIES' };
    }
    
    this.blood_bank.prbc_units -= blood_needed.prbc;
    this.blood_bank.ffp_units -= blood_needed.ffp;
    
    this.surgical_capacity.surgeries_today++;
    this.stats.total_surgeries_performed++;
    
    patient.dcs_performed = true;
    patient.needs_definitive_surgery = true;
    patient.survival_rate = 0.88;
    
    return {
      success: true,
      surgery_type: 'DAMAGE_CONTROL',
      duration_hours: 1.5,
      blood_used: blood_needed,
      patient_stable: true,
      needs_evacuation_to_role3: true,
      survival_rate: patient.survival_rate
    };
  }
  
  resuscitateWithBlood(patient, units_needed = 4) {
    if (this.blood_bank.prbc_units < units_needed) {
      if (!this.blood_bank.walking_blood_bank_activated) {
        this.activateWalkingBloodBank();
      }
      
      if (this.blood_bank.prbc_units < units_needed) {
        return {
          success: false,
          reason: 'INSUFFICIENT_BLOOD',
          available: this.blood_bank.prbc_units,
          needed: units_needed
        };
      }
    }
    
    this.blood_bank.prbc_units -= units_needed;
    patient.blood_transfused = true;
    patient.survival_improvement = (patient.survival_improvement || 1.0) * 1.5;
    
    return {
      success: true,
      units_transfused: units_needed,
      remaining_blood: this.blood_bank.prbc_units
    };
  }
  
  activateWalkingBloodBank() {
    const parent_brigade_strength = 4000;
    const potential_donors = Math.floor(parent_brigade_strength * 0.035);
    const compatible_units = Math.floor(potential_donors * 0.5);
    
    this.blood_bank.prbc_units += compatible_units;
    this.blood_bank.walking_blood_bank_activated = true;
    
    return {
      activated: true,
      units_collected: compatible_units,
      total_blood_now: this.blood_bank.prbc_units
    };
  }
  
  getSurgicalCapacityStatus() {
    const capacity_used = (this.surgical_capacity.surgeries_today / 
                           this.surgical_capacity.max_surgeries_per_day) * 100;
    
    let alert_level = 'GREEN';
    if (capacity_used >= 100) alert_level = 'RED';
    else if (capacity_used >= 75) alert_level = 'YELLOW';
    
    return {
      surgical_teams: this.surgical_teams,
      max_surgeries_per_day: this.surgical_capacity.max_surgeries_per_day,
      surgeries_performed_today: this.surgical_capacity.surgeries_today,
      capacity_remaining: this.surgical_capacity.max_surgeries_per_day - this.surgical_capacity.surgeries_today,
      capacity_used_percentage: capacity_used,
      backlog: this.surgical_capacity.surgical_backlog,
      alert_level: alert_level
    };
  }
  
  getBloodBankStatus(daily_consumption = 20) {
    const days_supply = daily_consumption > 0 ? this.blood_bank.prbc_units / daily_consumption : 999;
    
    let alert_level = 'GREEN';
    if (days_supply < 0.5) alert_level = 'BLACK';
    else if (days_supply < 1) alert_level = 'RED';
    else if (days_supply < 3) alert_level = 'YELLOW';
    
    return {
      prbc_units: this.blood_bank.prbc_units,
      ffp_units: this.blood_bank.ffp_units,
      platelet_units: this.blood_bank.platelet_units,
      days_supply: days_supply,
      walking_blood_bank_activated: this.blood_bank.walking_blood_bank_activated,
      alert_level: alert_level,
      critical: alert_level === 'BLACK' || alert_level === 'RED'
    };
  }
  
  resetDailySurgicalCounter() {
    this.surgical_capacity.surgeries_today = 0;
  }
  
  resupplyBlood(blood) {
    this.blood_bank.prbc_units += blood.prbc_units || 0;
    this.blood_bank.ffp_units += blood.ffp_units || 0;
    this.blood_bank.platelet_units += blood.platelet_units || 0;
  }
  
  // ============================================================================
  // OVERRIDE METHODS
  // ============================================================================
  
  getOperationalIssues() {
    const issues = super.getOperationalIssues();
    
    if (this.surgical_capacity.backlog > 0) {
      issues.push(`Surgical backlog: ${this.surgical_capacity.backlog} patients`);
    }
    
    const blood_status = this.getBloodBankStatus();
    if (blood_status.critical) {
      issues.push(`Critical blood shortage (${this.blood_bank.prbc_units} units, ${blood_status.days_supply.toFixed(1)} days)`);
    }
    
    if (this.supplies.surgical_supplies < 5) {
      issues.push('Low surgical supplies');
    }
    
    // Check evacuation assets
    const effective = this.getEffectiveAssets();
    if (effective.ground_ambulances.total < 4) {
      issues.push('Critically low ambulances (< 4)');
    }
    
    // Check supported Role 1s
    if (this.supported_role1s.length === 0) {
      issues.push('No Role 1s configured for evacuation support');
    }
    
    return issues;
  }
  
  toJSON() {
    return {
      ...super.toJSON(),
      parent_brigade_id: this.parent_brigade_id,
      brigade_type: this.brigade_type,
      hold_time_hours: this.hold_time_hours,
      can_perform_surgery: this.can_perform_surgery,
      can_perform_definitive_surgery: this.can_perform_definitive_surgery,
      surgical_capacity_status: this.getSurgicalCapacityStatus(),
      blood_bank_status: this.getBloodBankStatus(),
      supported_role1s: this.supported_role1s,
      pull_capacity: this.getPullCapacityByRole1()
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Already exported at definition

export default Role2BasicFacility;
